import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import PhoneNumberDisplay from '@/components/ui/PhoneNumberDisplay';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { ROUTE } from '@/config';
import { useFetchTeamMembers, useFetchRoles, useUpdateTeamMember } from '@/hooksApi';
import { MultiSelectChips } from '@/components/ui/multi-select-chips';
import { parsePhoneNumber, attachCountryCodeToPhone } from '@/utils/helpers/phoneNumberHelpers';

const SettingsTeamMembersEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const { data: teamMemberData, isLoading: isLoadingMembers } = useFetchTeamMembers();
  const { data: rolesData } = useFetchRoles();
  const { mutate: updateTeamMember, isLoading: isUpdating } = useUpdateTeamMember();

  const members = teamMemberData?.object || [];
  const roles = rolesData?.object || [];
  const member = members.find((m: any) => m.uid === id);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneCode, setPhoneCode] = useState('+964');
  const [nationalPhone, setNationalPhone] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (member) {
      setFirstName(member.first_name || '');
      setLastName(member.last_name || '');
      const parsed = parsePhoneNumber(member.phone);
      setPhoneCode(`+${parsed.countryCode || '964'}`);
      setNationalPhone(parsed.phone || '');
      setSelectedRoles(member.roles?.map((r: any) => r.uid) || []);
    }
  }, [member]);

  const handleCancel = () => {
    navigate(ROUTE.SETTINGS_TEAM_MEMBERS);
  };

  const handleSave = () => {
    if (!id) return;
    const errors: Record<string, boolean> = {};
    if (!firstName.trim()) errors.first_name = true;
    if (!lastName.trim()) errors.last_name = true;
    if (!nationalPhone.trim()) errors.phone_number = true;
    if (selectedRoles.length === 0) errors.roles = true;

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const missing = Object.keys(errors).map(k => String(t(k))).join(', ');
      toast({ title: t('required_fields_missing'), description: missing, variant: 'destructive' });
      return;
    }

    updateTeamMember(
      {
        uid: id,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: null,
        phone: attachCountryCodeToPhone(nationalPhone.trim(), phoneCode) || '',
        city_slug: '',
        role_uids: selectedRoles,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['teamMember-users']);
          toast({ title: t('member_updated') });
          navigate(ROUTE.SETTINGS_TEAM_MEMBERS);
        },
        onError: (error: any) => {
          const msg = error?.response?.data?.message || error?.message || t('operation_failed');
          toast({ title: t('error_title'), description: msg, variant: 'destructive' });
        },
      },
    );
  };

  if (isLoadingMembers) {
    return (
      <div className='sd-scope sd-font flex flex-col'>
        <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
          <SettingsBreadcrumb
            moduleName='team_members'
            moduleRoute={ROUTE.SETTINGS_TEAM_MEMBERS}
            action='edit_member'
          />
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8'>
            <div className='animate-pulse space-y-4'>
              <div className='h-4 bg-sd-muted rounded w-3/4'></div>
              <div className='h-4 bg-sd-muted rounded w-1/2'></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className='sd-scope sd-font flex flex-col'>
        <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
          <SettingsBreadcrumb
            moduleName='team_members'
            moduleRoute={ROUTE.SETTINGS_TEAM_MEMBERS}
            action='edit_member'
          />
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8 text-center'>
            <p className='text-sm text-sd-muted-foreground'>{t('member_not_found')}</p>
            <button
              onClick={handleCancel}
              className='mt-4 px-4 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90'
            >
              {t('back_to_list')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const inputClass = (field?: string) =>
    `w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-sd-ring focus:border-transparent placeholder:text-sd-muted-foreground ${
      field && fieldErrors[field] ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'
    }`;

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        <SettingsBreadcrumb
          moduleName='team_members'
          moduleRoute={ROUTE.SETTINGS_TEAM_MEMBERS}
          action='edit_member'
        />

        <h1 className='text-xl font-semibold text-sd-foreground'>{t('edit_member')}</h1>

        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
          <div className='px-6 py-4 border-b border-sd-border flex items-center gap-3'>
            <button
              onClick={handleCancel}
              className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
            >
              <ArrowLeft className='h-4 w-4' />
            </button>
            <h2 className='text-sm font-semibold text-sd-foreground'>{t('member_details')}</h2>
          </div>
          <div className='p-6 space-y-4'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div className='space-y-1.5'>
                <label className='text-xs font-medium text-sd-muted-foreground'>
                  {t('first_name')} *
                </label>
                <input
                  type='text'
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); setFieldErrors(prev => ({ ...prev, first_name: false })); }}
                  placeholder={t('first_name')}
                  className={inputClass('first_name')}
                />
                {fieldErrors.first_name && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
              </div>
              <div className='space-y-1.5'>
                <label className='text-xs font-medium text-sd-muted-foreground'>
                  {t('last_name')} *
                </label>
                <input
                  type='text'
                  value={lastName}
                  onChange={(e) => { setLastName(e.target.value); setFieldErrors(prev => ({ ...prev, last_name: false })); }}
                  placeholder={t('last_name')}
                  className={inputClass('last_name')}
                />
                {fieldErrors.last_name && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
              </div>
            </div>
            <div className='space-y-1.5'>
              <label className='text-xs font-medium text-sd-muted-foreground'>
                {t('phone_number')} *
              </label>
              <div className={fieldErrors.phone_number ? 'rounded-lg ring-1 ring-red-500' : ''}>
                <PhoneNumberDisplay phoneCode={phoneCode} nationalPhone={nationalPhone} onPhoneCodeChange={setPhoneCode} onNationalPhoneChange={(v) => { setNationalPhone(v); setFieldErrors(prev => ({ ...prev, phone_number: false })); }} />
              </div>
              {fieldErrors.phone_number && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
            </div>
            <div className='space-y-1.5'>
              <label className='text-xs font-medium text-sd-muted-foreground'>{t('roles')} *</label>
              <div className={fieldErrors.roles ? 'rounded-lg ring-1 ring-red-500' : ''}>
                <MultiSelectChips
                  options={roles.map((r: any) => ({ value: r.uid, label: r.name }))}
                  selected={selectedRoles}
                  onChange={(v) => { setSelectedRoles(v); setFieldErrors(prev => ({ ...prev, roles: false })); }}
                  placeholder={t('select_roles')}
                />
              </div>
              {fieldErrors.roles && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
            </div>
          </div>
          <div className='px-6 py-4 border-t border-sd-border flex justify-end gap-3'>
            <button
              onClick={handleCancel}
              className='px-4 py-2.5 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isUpdating}
              className='px-6 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed'
            >
              {isUpdating ? t('loading') : t('update_member')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsTeamMembersEdit;
